import { useState, useEffect, createContext, useContext } from 'react';
import { setupMockAuth } from '../services/mockAuth';

// Authentication Context
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [mockAuth, setMockAuth] = useState(null);

  // Setup mock auth for development
  useEffect(() => {
    if (import.meta.env.DEV) {
      const mock = setupMockAuth();
      setMockAuth(mock);
    }
  }, []);

  // Check authentication status
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await fetch('/.auth/me');
      if (response.ok) {
        const authPayload = await response.json();
        const user = authPayload.clientPrincipal;
        
        if (user) {
          setUser({
            id: user.userId,
            name: user.userDetails,
            email: user.userDetails,
            provider: user.identityProvider,
            roles: user.userRoles || ['authenticated']
          });
          setIsAuthenticated(true);
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.log('Auth check failed:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = (provider = 'github') => {
    if (import.meta.env.DEV && mockAuth) {
      // Use mock authentication in development
      mockAuth.login(provider).then(() => {
        checkAuthStatus();
      });
    } else {
      // Redirect to Azure Static Web Apps auth provider in production
      window.location.href = `/.auth/login/${provider}?post_login_redirect_uri=${encodeURIComponent(window.location.origin + '/translate')}`;
    }
  };

  const logout = async () => {
    try {
      if (import.meta.env.DEV && mockAuth) {
        // Use mock authentication in development
        await mockAuth.logout();
        setUser(null);
        setIsAuthenticated(false);
      } else {
        // Redirect to Azure Static Web Apps logout in production
        window.location.href = `/.auth/logout?post_logout_redirect_uri=${encodeURIComponent(window.location.origin)}`;
      }
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};